import Card from '$tablet-app/src/app/components/Card';
import HeaderBar from '$tablet-app/src/app/components/HeaderBar';
import MonthGrid from '$tablet-app/src/app/components/MonthGrid';
import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Tab from '$tablet-app/src/app/components/Tab';
import Text from '$tablet-app/src/app/components/Text';
import { palette } from '$tablet-app/src/app/theme';
import { getGoalProductivityData, Goal } from '@akin-home/goals';
import React, { FC, useMemo, useState } from 'react';
import { FlatList, View } from 'react-native';
import styled from 'styled-components/native';
import GoalCompletionStatsCard from '../GoalCompletionStatsCard';
import GoalLibraryCard from '../GoalLibraryCard';

import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import { LineChart } from 'react-native-chart-kit';

interface Props {
  tab?: 'summary' | 'goals';

  goals?: Goal[];

  activeGoal?: Goal;

  period?: 'day' | 'week' | 'month';

  user_id?: string;

  onPressBack?: () => void;

  onChangeTab?: (tab: 'summary' | 'goals') => void;

  onChangePeriod?: (period: 'day' | 'week' | 'month') => void;

  onSelectGoal?: (goal: Goal) => void;
}

const GoalStatisticsScreenView: FC<Props> = (props) => {
  const {
    tab = 'summary',
    user_id,
    period = 'day',
    goals,
    activeGoal,
    onPressBack,
    onChangeTab,
    onSelectGoal,
  } = props;

  const [layout, setLineChartContainerLayout] = useState({
    width: 0,
    height: 0,
  });

  const onPressTab = (tab: 'summary' | 'goals') => () => onChangeTab?.(tab);

  const onPressPeriodTab = (period: 'day' | 'week' | 'month') => () =>
    props.onChangePeriod?.(period);

  const onPressGoal = (goal: Goal) => () => onSelectGoal?.(goal);

  const dateString = new Date().toISOString().split('T')[0];

  const onLineChartContainerLayout = ({ nativeEvent: { layout } }: any) => {
    setLineChartContainerLayout({
      width: layout.width,
      height: layout.height,
    });
  };

  const data = useMemo(
    () => {
      const data =
        goals?.length && user_id
          ? getGoalProductivityData(goals, {
              user_id,
              period,
              date: new Date(),
            })
          : { labels: [], datasets: [{ data: [100] }] };

      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goals, user_id, period, dateString]
  );

  return (
    <Screen>
      <HeaderBar
        left={
          onPressBack ? (
            <NewButton
              variant="secondary"
              icon="chevron-left"
              onPress={onPressBack}
            >
              Back
            </NewButton>
          ) : null
        }
        center={
          <Text variant="h3" weight="bold">
            Statistics
          </Text>
        }
      />

      <Spacer height="1px" color="lightgrey" />

      <TabRow>
        <StyledTab onPress={onPressTab('summary')} active={tab === 'summary'}>
          Summary
        </StyledTab>
        <StyledTab onPress={onPressTab('goals')} active={tab === 'goals'}>
          Goals
        </StyledTab>
      </TabRow>

      {tab === 'summary' ? (
        <Row flex={1} padding={16}>
          <SummaryPageCard>
            <Row padding={16}>
              <Text variant="caption">PRODUCTIVITY</Text>
            </Row>

            <Spacer height="8px" />

            <Row>
              <Tab active={period === 'day'} onPress={onPressPeriodTab('day')}>
                Daily
              </Tab>
              <Tab
                active={period === 'week'}
                onPress={onPressPeriodTab('week')}
              >
                Weekly
              </Tab>
              <Tab
                active={period === 'month'}
                onPress={onPressPeriodTab('month')}
              >
                Monthly
              </Tab>
            </Row>

            <LineChartOuterContainer>
              <LineChartContainer onLayout={onLineChartContainerLayout}>
                <LineChart
                  style={line_chart_style}
                  width={layout.width}
                  height={layout.height}
                  yAxisSuffix="%"
                  yAxisInterval={20}
                  chartConfig={{
                    backgroundColor: '#F5F7F9',
                    backgroundGradientFrom: '#F5F7F9',
                    backgroundGradientTo: '#F5F7F9',
                    color: () => `#10B981`,
                    decimalPlaces: 0,
                  }}
                  withDots={false}
                  withVerticalLines={false}
                  withOuterLines={false}
                  withShadow={false}
                  fromZero
                  data={data}
                />
              </LineChartContainer>
            </LineChartOuterContainer>
          </SummaryPageCard>

          <Spacer width="16px" />

          <SummaryPageCard>
            <StreaksCardInner>
              <Text variant="caption">STREAKS</Text>

              <View>
                <FlatList
                  horizontal
                  data={goals}
                  keyExtractor={({ id }) => id}
                  renderItem={({ item }) => (
                    <CardWrapper>
                      <GoalLibraryCard
                        goal={item}
                        style={{
                          backgroundColor:
                            activeGoal?.id === item.id
                              ? palette.pastel_green
                              : palette.white,
                        }}
                        onPress={onPressGoal(item)}
                      />
                    </CardWrapper>
                  )}
                />
              </View>

              <Spacer height="16px" />

              {user_id ? (
                <StyledMonthGrid
                  date={new Date()}
                  data={
                    activeGoal?.getGoalMonthGridData(new Date(), user_id) || {}
                  }
                />
              ) : null}
            </StreaksCardInner>
          </SummaryPageCard>
        </Row>
      ) : (
        <Row padding={16} paddingRight={0} flexWrap="wrap">
          {user_id
            ? goals?.map((goal) => (
                <StatCard key={goal.id} goal={goal} user_id={user_id} />
              ))
            : null}
        </Row>
      )}
    </Screen>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const CardWrapper = styled.View`
  padding: 4px;
  padding-right: 16px;
`;

const TabRow = styled(Row)`
  background-color: white;
`;

const StyledTab = styled(Tab)`
  padding: 16px;
`;

const SummaryPageCard = styled(Card)`
  flex: 1;
`;

const StreaksCardInner = styled.View`
  flex: 1;
  padding: 16px;
`;

const StyledMonthGrid = styled(MonthGrid)`
  flex: 1;
`;

const StatCard = styled(GoalCompletionStatsCard)`
  margin-right: 16px;
  margin-bottom: 16px;
  max-width: 270px;
`;

const LineChartOuterContainer = styled.View`
  flex-direction: row;
  flex: 1;
  padding: 16px;
`;

const LineChartContainer = styled.View`
  flex-direction: row;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const line_chart_style = { borderRadius: 20 };

export default GoalStatisticsScreenView;
